import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import {
  Search,
  FileText,
  DollarSign,
  Calendar,
  CreditCard,
  AlertCircle,
  Loader2,
  CheckCircle,
  Shield,
  Lock,
  ArrowRight,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const InvoiceLookup = () => {
  const [invoiceId, setInvoiceId] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!invoiceId.trim()) return;
    setLoading(true);
    setError(null);
    setInvoice(null);
    try {
      const res = await fetch(`${API}/api/pay/lookup/${invoiceId.trim()}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Invoice not found");
      }
      setInvoice(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amt) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amt);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "N/A";

  return (
    <div className="overflow-hidden">
      <SEO
        title="Pay Invoice | RNB Consulting Group"
        description="Securely view and pay your RNB Consulting Group invoice online using Stripe."
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden bg-rnb-navy" data-testid="invoice-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-rnb-navy via-[#1a3050] to-rnb-blue" />

        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 right-32 w-64 h-64 bg-rnb-teal/15 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 left-16 w-80 h-80 bg-rnb-blue/20 rounded-full blur-3xl animate-float" style={{ animationDelay: "2s" }} />
          <div className="absolute inset-0 opacity-[0.06]" style={{
            backgroundImage: "linear-gradient(rgba(31,198,183,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(31,198,183,0.4) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Copy */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
                <Sparkles className="w-4 h-4 text-rnb-teal" />
                <span className="text-sm font-medium">Secure Online Payments</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-4 leading-[1.1]" data-testid="hero-heading">
                Pay Your Invoice
              </h1>
              <p className="text-lg text-white/70 max-w-md mb-8 leading-relaxed">
                Enter your Invoice ID to securely view details and complete your payment online. Fast, easy, and protected by Stripe.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4">
                {[
                  { icon: Shield, label: "256-bit Encryption" },
                  { icon: Lock, label: "PCI Compliant" },
                  { icon: CreditCard, label: "Cards, ACH & More" },
                ].map((b) => (
                  <div key={b.label} className="flex items-center gap-2 text-white/50 text-xs">
                    <b.icon className="w-3.5 h-3.5 text-rnb-teal/70" />
                    <span>{b.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Lookup Card */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-rnb-teal/20 to-rnb-blue/20 rounded-3xl blur-xl" />
              <div className="relative bg-white/[0.07] backdrop-blur-xl border border-white/15 rounded-2xl p-8 shadow-2xl" data-testid="lookup-card">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-rnb-teal/20 rounded-xl flex items-center justify-center">
                    <FileText className="w-5 h-5 text-rnb-teal" />
                  </div>
                  <div>
                    <h2 className="text-white font-semibold text-base">Invoice Lookup</h2>
                    <p className="text-white/40 text-xs">Find your invoice by ID</p>
                  </div>
                </div>

                <form onSubmit={handleLookup}>
                  <label className="text-white/60 text-xs font-medium mb-2 block">Invoice ID</label>
                  <div className="flex gap-2.5">
                    <div className="relative flex-1">
                      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <Input
                        value={invoiceId}
                        onChange={(e) => setInvoiceId(e.target.value)}
                        placeholder="e.g. inv_abc123"
                        className="pl-10 h-12 text-sm rounded-xl bg-white/10 border-white/15 text-white placeholder:text-white/30 focus:ring-2 focus:ring-rnb-teal/40 focus:border-rnb-teal/40"
                        data-testid="invoice-id-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading || !invoiceId.trim()}
                      className="h-12 px-6 bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-xl font-semibold shadow-lg shadow-rnb-teal/25 transition-all"
                      data-testid="lookup-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                        <span className="flex items-center gap-2">Look Up <ArrowRight className="w-4 h-4" /></span>
                      )}
                    </Button>
                  </div>
                </form>

                {/* Error */}
                {error && (
                  <div className="mt-5 bg-red-500/10 border border-red-400/20 rounded-xl p-4 flex items-start gap-3" data-testid="lookup-error">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-300">{error}</p>
                      <p className="text-xs text-red-400/70 mt-1">Double-check your Invoice ID and try again.</p>
                    </div>
                  </div>
                )}

                {/* Need help */}
                <p className="text-center text-white/30 text-[11px] mt-5">
                  Your Invoice ID is in the email we sent you.{" "}
                  <a href="mailto:info@rnbconsultinggroup.com" className="text-rnb-teal/70 hover:text-rnb-teal underline">Need help?</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Invoice Result */}
      {invoice && (
        <section className="bg-[#f0f4f8] py-16" data-testid="invoice-result-section">
          <div className="max-w-2xl mx-auto px-4">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100" data-testid="invoice-result">
              {/* Invoice Header */}
              <div className="relative bg-gradient-to-r from-rnb-navy to-rnb-blue px-8 py-7 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-rnb-teal rounded-full blur-2xl" />
                </div>
                <div className="relative flex items-center justify-between">
                  <div>
                    <p className="text-white/50 text-xs tracking-wide uppercase font-medium mb-1">Invoice</p>
                    <p className="text-white font-semibold text-lg font-mono tracking-wide">{invoice.invoice_id}</p>
                  </div>
                  <Badge
                    className={`px-3 py-1 text-xs font-semibold tracking-wide ${
                      invoice.status === "paid"
                        ? "bg-emerald-400/20 text-emerald-200 border border-emerald-400/30"
                        : invoice.status === "overdue"
                        ? "bg-red-400/20 text-red-200 border border-red-400/30"
                        : "bg-amber-400/20 text-amber-200 border border-amber-400/30"
                    }`}
                    data-testid="invoice-status-badge"
                  >
                    {invoice.status?.toUpperCase()}
                  </Badge>
                </div>
              </div>

              {/* Invoice Body */}
              <div className="p-8 space-y-0">
                {[
                  { label: "Bill To", value: invoice.client_name },
                  { label: "Description", value: invoice.description },
                  ...(invoice.due_date
                    ? [{ label: "Due Date", value: formatDate(invoice.due_date), icon: Calendar }]
                    : []),
                ].map((row, i) => (
                  <div key={row.title || row.name || row.label || i} className="flex items-center justify-between py-4 border-b border-gray-100 last:border-b-0">
                    <span className="text-sm text-gray-400 flex items-center gap-1.5">
                      {row.icon && <row.icon className="w-3.5 h-3.5" />}
                      {row.label}
                    </span>
                    <span className="text-sm font-medium text-rnb-navy text-right max-w-[280px]">{row.value}</span>
                  </div>
                ))}

                {/* Amount Highlight */}
                <div className="bg-gradient-to-r from-rnb-light to-[#e8f7f6] rounded-xl p-6 mt-6" data-testid="invoice-amount">
                  <div className="flex items-center justify-between">
                    <span className="text-base font-semibold text-rnb-navy">Total Due</span>
                    <div className="flex items-baseline gap-0.5">
                      <span className="text-sm text-rnb-blue font-medium">$</span>
                      <span className="text-4xl font-bold text-rnb-navy font-mono tracking-tight">
                        {invoice.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-6">
                  {invoice.status === "paid" ? (
                    <div className="flex items-center justify-center gap-2.5 py-5 bg-emerald-50 rounded-xl border border-emerald-100" data-testid="invoice-paid-badge">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                      <span className="text-sm font-semibold text-emerald-700">This invoice has been paid in full</span>
                    </div>
                  ) : (
                    <a href={invoice.payment_link} className="block">
                      <Button
                        className="w-full h-14 text-base font-semibold bg-rnb-teal hover:bg-rnb-teal/90 text-white rounded-xl shadow-lg shadow-rnb-teal/20 transition-all hover:shadow-xl hover:shadow-rnb-teal/30 hover:-translate-y-0.5"
                        data-testid="pay-invoice-btn"
                      >
                        <CreditCard className="w-5 h-5 mr-2.5" />
                        Pay {formatCurrency(invoice.amount)} Securely
                      </Button>
                    </a>
                  )}
                </div>

                {/* Payment Trust Line */}
                <div className="flex items-center justify-center gap-6 pt-5 mt-2">
                  {[
                    { icon: Shield, text: "Secure checkout" },
                    { icon: Lock, text: "Encrypted" },
                    { icon: CreditCard, text: "Powered by Stripe" },
                  ].map((t) => (
                    <div key={t.text} className="flex items-center gap-1.5 text-gray-300 text-[11px]">
                      <t.icon className="w-3 h-3" />
                      <span>{t.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Info Section (when no invoice shown) */}
      {!invoice && (
        <section className="bg-[#f0f4f8] py-16" data-testid="info-section">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl font-bold text-rnb-navy mb-2">How It Works</h2>
              <p className="text-gray-400 text-sm">Three simple steps to pay your invoice online</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  step: "1",
                  title: "Find Your Invoice ID",
                  desc: "Check the email we sent you or your client portal. Your Invoice ID starts with inv_.",
                  icon: Search,
                },
                {
                  step: "2",
                  title: "Review Details",
                  desc: "Verify the invoice amount, description, and due date match your records.",
                  icon: FileText,
                },
                {
                  step: "3",
                  title: "Pay Securely",
                  desc: "Complete payment via Stripe. We accept all major cards, ACH transfers, and more.",
                  icon: CreditCard,
                },
              ].map((s) => (
                <div key={s.step} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow group">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rnb-navy to-rnb-blue flex items-center justify-center text-white text-xs font-bold shadow-sm">
                      {s.step}
                    </div>
                    <s.icon className="w-4 h-4 text-rnb-teal" />
                  </div>
                  <h3 className="font-heading font-semibold text-rnb-navy text-sm mb-1.5">{s.title}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default InvoiceLookup;
